import {SKETCH_PARAMETRIC_TOLERANCE} from './SketchParametricToleranceConfig';

/**
 * 硬约束 Jacobian 零空间基计算器。
 *
 * 职责：通过带主元的行最简形求出 `J·δ = 0` 的正交基。偏好层只允许沿这些基方向移动，
 * 因而不会用软权重交换或放松任何已提交几何关系、driving 尺寸、Fix 或外部边界。
 */
export class SketchParametricNullSpaceProjector {
    /**
     * 计算矩阵零空间的正交基。
     *
     * @param matrix 硬约束 Jacobian（行=残差，列=自由参数）。
     * @param columnCount 自由参数数量；空矩阵时必须显式提供。
     * @returns 基向量数组；每个基向量长度等于 columnCount。
     */
    public compute(
        matrix: readonly (readonly number[])[],
        columnCount: number,
    ): readonly (readonly number[])[] {
        if (!Number.isInteger(columnCount) || columnCount < 0) {
            throw new Error(`零空间列数必须是非负整数：${String(columnCount)}`);
        }
        if (columnCount === 0) {
            return [];
        }
        if (matrix.some((row) => row.length !== columnCount)) {
            throw new Error('硬约束 Jacobian 列数不一致，无法计算零空间');
        }
        if (matrix.length === 0) {
            return Array.from({length: columnCount}, (_item, index) => (
                Array.from({length: columnCount}, (_entry, columnIndex) => (
                    columnIndex === index ? 1 : 0
                ))
            ));
        }

        const reduced = matrix.map((row) => [...row]);
        const pivotColumns: number[] = [];
        let pivotRow = 0;

        for (let column = 0; column < columnCount && pivotRow < reduced.length; column += 1) {
            let selectedRow = pivotRow;

            for (let row = pivotRow + 1; row < reduced.length; row += 1) {
                if (Math.abs(reduced[row]![column] ?? 0) > Math.abs(reduced[selectedRow]![column] ?? 0)) {
                    selectedRow = row;
                }
            }

            const pivot = reduced[selectedRow]![column] ?? 0;

            if (Math.abs(pivot) <= SKETCH_PARAMETRIC_TOLERANCE.nullSpacePivot) {
                continue;
            }
            if (selectedRow !== pivotRow) {
                const current = reduced[pivotRow]!;

                reduced[pivotRow] = reduced[selectedRow]!;
                reduced[selectedRow] = current;
            }

            const normalizedPivot = reduced[pivotRow]![column]!;

            for (let entry = column; entry < columnCount; entry += 1) {
                reduced[pivotRow]![entry] = (reduced[pivotRow]![entry] ?? 0) / normalizedPivot;
            }
            for (let row = 0; row < reduced.length; row += 1) {
                if (row === pivotRow) {
                    continue;
                }
                const factor = reduced[row]![column] ?? 0;

                if (Math.abs(factor) <= SKETCH_PARAMETRIC_TOLERANCE.nullSpacePivot) {
                    continue;
                }
                for (let entry = column; entry < columnCount; entry += 1) {
                    reduced[row]![entry] = (reduced[row]![entry] ?? 0)
                        - factor * (reduced[pivotRow]![entry] ?? 0);
                }
            }

            pivotColumns.push(column);
            pivotRow += 1;
        }

        const pivotColumnSet = new Set(pivotColumns);
        const freeColumns = Array.from({length: columnCount}, (_item, index) => index)
            .filter((column) => !pivotColumnSet.has(column));
        const rawBasis = freeColumns.map((freeColumn) => {
            const vector = Array<number>(columnCount).fill(0);

            vector[freeColumn] = 1;
            pivotColumns.forEach((pivotColumn, rowIndex) => {
                vector[pivotColumn] = -(reduced[rowIndex]![freeColumn] ?? 0);
            });
            return vector;
        });

        return this.orthonormalize(rawBasis);
    }

    /**
     * 使用修正 Gram-Schmidt 把零空间基正交归一化。
     *
     * @param basis 原始零空间基。
     * @returns 正交单位基；数值退化向量被移除。
     */
    private orthonormalize(basis: readonly (readonly number[])[]): readonly (readonly number[])[] {
        const orthonormal: number[][] = [];

        basis.forEach((source) => {
            const vector = [...source];

            orthonormal.forEach((existing) => {
                const projection = vector.reduce((sum, value, index) => (
                    sum + value * (existing[index] ?? 0)
                ), 0);

                for (let index = 0; index < vector.length; index += 1) {
                    vector[index] = (vector[index] ?? 0) - projection * (existing[index] ?? 0);
                }
            });

            const length = Math.sqrt(vector.reduce((sum, value) => sum + value * value, 0));

            if (length <= SKETCH_PARAMETRIC_TOLERANCE.nullSpacePivot) {
                return;
            }
            orthonormal.push(vector.map((value) => value / length));
        });

        return orthonormal;
    }
}
